import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import { ThemeProvider } from './context/ThemeContext';
import './i18n'; // Initialize internationalization
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import AdminLayout from './components/AdminLayout';
import RequireRole from './components/RequireRole';
import Home from './pages/Home';
import Standings from './pages/Standings';
import GamePeriods from './pages/GamePeriods';
import Stats from './pages/Stats';
import Playoffs from './pages/Playoffs';
import PlayoffSeries from './pages/PlayoffSeries';
import Finals from './pages/Finals';
import Champions from './pages/Champions';
import Awards from './pages/Awards';
import FormerChampions from './pages/FormerChampions';
import Rules from './pages/Rules';
import About from './pages/About';
import Login from './pages/Login';
import Register from './pages/Register';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import Teams from './pages/Teams';
import TeamRoster from './pages/TeamRoster';
import GameDetail from './pages/GameDetail';
import AllGames from './pages/AllGames';
import PlayerDetail from './pages/PlayerDetail';
import Players from './pages/Players';
import ComparePlayers from './pages/ComparePlayers';
import OwnerNews from './pages/OwnerNews';
import OwnerDetail from './pages/OwnerDetail';
import TradeNews from './pages/TradeNews';
import TeamOwners from './pages/TeamOwners';
import Settings from './pages/Settings';
import Apply from './pages/Apply';
import PublicDraftBoard from './pages/PublicDraftBoard';
import AdminDashboard from './pages/admin/AdminDashboard';
import ManageRookies from './pages/admin/ManageRookies';
import RosterManagement from './pages/admin/RosterManagement';
import SeasonManagement from './pages/admin/SeasonManagement';
import GamesManagement from './pages/admin/GamesManagement';
import PlayoffManagement from './pages/admin/PlayoffManagement';
import AdminDocumentation from './pages/admin/AdminDocumentation';
import GMDocumentation from './pages/admin/GMDocumentation';
import FantasyDraftManagement from './pages/admin/FantasyDraftManagement';
import FreeAgencyManagement from './pages/admin/FreeAgencyManagement';
import FreeAgencyPage from './pages/FreeAgencyPage';
import TradeManagement from './components/TradeManagement';
import InviteCodeManagement from './components/InviteCodeManagement';
import UserManagement from './components/UserManagement';
import PlayerManagement from './components/PlayerManagement';
import ApplicationManagement from './components/ApplicationManagement';
import Analytics from './pages/Analytics';
import AnalyticsTeamRankings from './pages/analytics/TeamRankings';
import AnalyticsGMRankings from './pages/analytics/GMRankings';
import AnalyticsTeamDetail from './pages/analytics/TeamDetail';
import AnalyticsGMDetail from './pages/analytics/GMDetail';
import HeadToHead from './pages/analytics/HeadToHead';
import ComplianceAnalytics from './pages/analytics/ComplianceAnalytics';

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <Router>
        <Routes>
          {/* Public routes with Navbar and Footer */}
          <Route
            path="/*"
            element={
              <div className="min-h-screen bg-gray-100 dark:bg-gray-900 text-gray-900 dark:text-gray-100 flex flex-col">
                <Navbar />
                <main className="flex-grow">
                  <Routes>
                    <Route path="/" element={<Home />} />
                    <Route path="/standings" element={<Standings />} />
                    <Route path="/game-periods" element={<GamePeriods />} />
                    <Route path="/stats" element={<Stats />} />
                    <Route path="/playoffs" element={<Playoffs />} />
                    <Route path="/playoffs/series/:id" element={<PlayoffSeries />} />
                    <Route path="/finals" element={<Finals />} />
                    <Route path="/champions" element={<Champions />} />
                    <Route path="/awards" element={<Awards />} />
                    <Route path="/former-champions" element={<FormerChampions />} />
                    <Route path="/rules" element={<Rules />} />
                    <Route path="/about" element={<About />} />
                    <Route path="/team-owners" element={<TeamOwners />} />
                    <Route path="/apply" element={<Apply />} />
                    <Route path="/settings" element={<Settings />} />
                    <Route path="/login" element={<Login />} />
                    <Route path="/register" element={<Register />} />
                    <Route path="/forgot-password" element={<ForgotPassword />} />
                    <Route path="/reset-password" element={<ResetPassword />} />
                    <Route path="/teams" element={<Teams />} />
                    <Route path="/teams/:id" element={<TeamRoster />} />
                    <Route path="/players" element={<Players />} />
                    <Route path="/compare" element={<ComparePlayers />} />
                    <Route path="/games" element={<AllGames />} />
                    <Route path="/game/:id" element={<GameDetail />} />
                    <Route path="/player/:id" element={<PlayerDetail />} />
                    <Route path="/news/owners" element={<OwnerNews />} />
                    <Route path="/owner/:id" element={<OwnerDetail />} />
                    <Route path="/news/trades" element={<TradeNews />} />\n                    <Route path="/draft/:draftId" element={<PublicDraftBoard />} />
                    <Route path="/free-agency/:seasonId" element={<FreeAgencyPage />} />
                    <Route path="/analytics" element={<Analytics />} />
                    <Route path="/analytics/team-rankings" element={<AnalyticsTeamRankings />} />
                    <Route path="/analytics/gm-rankings" element={<AnalyticsGMRankings />} />
                    <Route path="/analytics/team/:teamId" element={<AnalyticsTeamDetail />} />
                    <Route path="/analytics/gm/:userId" element={<AnalyticsGMDetail />} />
                    <Route path="/analytics/head-to-head" element={<HeadToHead />} />
                    <Route path="/analytics/compliance" element={<ComplianceAnalytics />} />
                  </Routes>
                </main>
                <Footer />
              </div>
            }
          />

          {/* Admin routes without Navbar/Footer, protected by role */}
          <Route
            path="/admin/*"
            element={
              <RequireRole allowedRoles={['admin', 'owner', 'team_owner']}>
                <AdminLayout />
              </RequireRole>
            }
          >
            <Route index element={<AdminDashboard />} />
            <Route path="season" element={<SeasonManagement />} />
            <Route path="games" element={<GamesManagement />} />
            <Route path="playoffs" element={<PlayoffManagement />} />
            <Route path="fantasy-draft/:seasonId" element={<FantasyDraftManagement />} />
            <Route path="free-agency/:seasonId" element={<FreeAgencyManagement />} />
            <Route path="players" element={<PlayerManagement />} />
            <Route path="roster" element={<RosterManagement />} />
            <Route path="trades" element={<TradeManagement />} />
            <Route path="teams" element={<div>Team Management (Coming Soon)</div>} />
            <Route path="invite-codes" element={<InviteCodeManagement />} />
            <Route path="applications" element={<ApplicationManagement />} />
            <Route path="users" element={<UserManagement />} />
            <Route path="documentation" element={<AdminDocumentation />} />
            <Route path="rookies" element={<ManageRookies />} />
            <Route path="gm-documentation" element={<GMDocumentation />} />
            <Route path="settings" element={<Navigate to="/settings" replace />} />
          </Route>
        </Routes>
      </Router>
    </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
